import React from "react";
import { Link } from "react-router-dom";
import SensorIcon from "./SensorIcon";
import { ChevronRight, Info } from "lucide-react";

// Tiny sparkline rendered as SVG
function Spark({ data = [], stroke = "#ffffff" }) {
  if (!data.length) return null;
  const w = 120;
  const h = 28;
  const min = Math.min(...data.map((d) => d.value));
  const max = Math.max(...data.map((d) => d.value));
  const range = max - min || 1;
  const points = data
    .map((d, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((d.value - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const last = data[data.length - 1];
  const lastX = w;
  const lastY = h - ((last.value - min) / range) * h;
  return (
    <svg width={w} height={h} className="overflow-visible">
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        points={`0,${h} ${points} ${w},${h}`}
        fill="url(#sg)"
        stroke="none"
      />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.4" fill={stroke} />
    </svg>
  );
}

export default function SensorCard({ sensor, data = [] }) {
  const hasValue = sensor.current !== "—" && sensor.current != null;
  return (
    <Link
      to={`/sensor/${sensor.id}`}
      className="group relative block overflow-hidden rounded-[22px] p-4 pb-3 transition-transform duration-300 hover:-translate-y-0.5 active:scale-[0.99]"
      style={{
        background:
          "linear-gradient(155deg, #10a78c 0%, #0b8d74 45%, #0a5d52 100%)",
        boxShadow:
          "0 8px 24px -12px rgba(0,168,138,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
      }}
    >
      {/* subtle glow */}
      <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-white/10 blur-2xl pointer-events-none" />

      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
          <SensorIcon name={sensor.icon} size={18} className="text-white" />
        </div>
        <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center ring-1 ring-white/25 group-hover:bg-white/30 transition-colors">
          <ChevronRight size={14} className="text-white" />
        </div>
      </div>

      <div className="mt-3">
        {data && data.length > 0 ? (
          <Spark data={data} />
        ) : (
          // Sparkline vuota: linea piatta tratteggiata per dare struttura visuale
          <svg width={120} height={28} className="overflow-visible">
            <line
              x1="0"
              y1="14"
              x2="120"
              y2="14"
              stroke="rgba(255,255,255,0.25)"
              strokeWidth="1.2"
              strokeDasharray="3 3"
            />
          </svg>
        )}
      </div>

      <div className="mt-2 flex items-end justify-between">
        <div>
          <div className="text-white text-[28px] font-semibold leading-none tracking-tight">
            {hasValue ? sensor.current : "—"}
            <span className="text-xs font-medium opacity-80 ml-1">
              {sensor.title}
            </span>
          </div>
          <div className="mt-2 text-[12.5px] text-white/90 leading-tight max-w-[130px] flex items-center gap-1">
            {hasValue ? sensor.subtitle : "In attesa dati"}
            <Info size={12} className="opacity-70" />
          </div>
        </div>
      </div>
    </Link>
  );
}
